<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import WhatsappPhone from '../../../../../components-next/Campaigns/Pages/CampaignPage/CreateCampaign/WhatsappPhone.vue';

const modelTemplate = defineModel('template', {
  type: Object,
  default: () => ({ name: '', language: '' }),
});

const selectedWhatsappNumber = defineModel('selectedWhatsappNumber', {
  type: String,
  default: '',
});

// Estado local
const templates = ref([]);
const isLoading = ref(true);
const error = ref(null);

// Datos dinámicos obtenidos desde backend
const accessToken = ref('');
const businessAccountId = ref('');

// 1️⃣ Obtener configuración de WhatsApp desde tu API local
const fetchWhatsappConfig = async () => {
  try {
    const res = await fetch(
      `/api/v1/custom_querys/whatsapp_config?phone_number=${encodeURIComponent(
        selectedWhatsappNumber.value
      )}`,
      {
        headers: {
          custom_access_token: '1a2b3c4d5e',
        },
      }
    );

    if (!res.ok) throw new Error('Error al obtener configuración de WhatsApp');

    const data = await res.json();
    accessToken.value = data.provider_config.api_key;
    businessAccountId.value = data.provider_config.business_account_id;
  } catch (e) {
    console.error('❌ Error al obtener config de WhatsApp:', e);
    error.value = 'No se pudo obtener configuración de Meta.';
  }
};

// 2️⃣ Obtener templates desde la API de Meta con token dinámico
const fetchTemplates = async () => {
  if (!accessToken.value || !businessAccountId.value) return;

  isLoading.value = true;
  error.value = null;
  try {
    const res = await fetch(
      `https://graph.facebook.com/v19.0/${businessAccountId.value}/message_templates`,
      {
        headers: {
          Authorization: `Bearer ${accessToken.value}`,
        },
      }
    );
    const data = await res.json();
    templates.value = data.data || [];
  } catch (e) {
    error.value = 'Error al cargar las plantillas.';
    console.error(e);
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  await fetchWhatsappConfig();
});

watch(selectedWhatsappNumber, async (newNumber) => {
  if (newNumber) {
    await fetchWhatsappConfig();
  }
});


watch([accessToken, businessAccountId], async ([token, account]) => {
  if (token && account) {
    await fetchTemplates();
  }
});
</script>

<template>
  <div class="flex flex-col gap-3">
    <label class="text-base font-semibold text-n-slate-12"
      >Seleccionar plantilla de WhatsApp</label
    >

    <div
      class="border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 shadow-sm p-4"
    >
      <p class="text-sm text-n-slate-11 mb-2">
        Elige una plantilla disponible en tu cuenta de Meta.
      </p>

      <!-- Cargando o error -->
      <div v-if="isLoading" class="text-n-slate-10 text-sm">
        Cargando plantillas...
      </div>
      <div v-else-if="error" class="text-red-500 text-sm">{{ error }}</div>
      <div v-else-if="templates.length === 0" class="text-n-slate-10 text-sm">
        No se encontraron plantillas.
      </div>

      <select
        v-else
        v-model="modelTemplate"
        class="w-full px-3 py-2 h-10 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm text-n-slate-12 transition"
      >
        <option disabled :value="{ name: '', language: '' }">
          -- Selecciona una plantilla --
        </option>
        <option
          v-for="tpl in templates"
          :key="tpl.id"
          :value="{ name: tpl.name, language: tpl.language }"
        >
          {{ tpl.name }} ({{ tpl.language }})
        </option>
      </select>

      <div
        v-if="modelTemplate.name"
        class="mt-5 rounded-lg bg-slate-50 dark:bg-slate-800 px-4 py-3 border border-slate-200 dark:border-slate-600"
      >
        <WhatsappPhone
          :message="
            templates
              .find(t => t.name === modelTemplate.name)
              ?.components.find(c => c.type === 'BODY')?.text ||
            'Sin contenido disponible'
          "
        />
      </div>
    </div>
  </div>
</template>
